import { useEffect, useRef, useState } from 'react'

import { logger } from 'shared/services/logger'
import type { AnimatedWallpaperSize } from 'shared/wallpapers/animatedWallpaper.types'

import styles from './WaterLayer.module.scss'
import type { WaterLayerProps } from './WaterLayer.types'
import { waterFragmentShader, waterVertexShader } from './waterShaders'

const cssSize = (size: AnimatedWallpaperSize) =>
  size.maxPixels
    ? `min(${size.value}${size.unit}, ${size.maxPixels}px)`
    : `${size.value}${size.unit}`

const loadImage = (url: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.crossOrigin = 'anonymous'
    image.onload = () => {
      void image
        .decode()
        .then(() => resolve(image))
        .catch(reject)
    }
    image.onerror = reject
    image.src = url
  })

export const WaterLayer = ({
  layer,
  assetBaseUrl,
  ready,
  onLoaded,
  onFailed
}: WaterLayerProps) => {
  const ref = useRef<HTMLCanvasElement>(null)
  const [fallback, setFallback] = useState(false)
  const readyRef = useRef(ready)
  useEffect(() => {
    readyRef.current = ready
  }, [ready])

  // Depending on fallback runs GPU/animation cleanup when the canvas unmounts.
  useEffect(() => {
    const canvas = ref.current
    if (!canvas) return
    const gl = canvas.getContext('webgl', {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false
    })
    if (!gl) {
      setFallback(true)
      return
    }
    let disposed = false
    let frame = 0
    let observer: ResizeObserver | undefined
    const shaders: WebGLShader[] = []
    const textures: WebGLTexture[] = []
    let program: WebGLProgram | null = null
    let buffer: WebGLBuffer | null = null
    const lost = (event: Event) => {
      event.preventDefault()
      if (!disposed) setFallback(true)
    }
    canvas.addEventListener('webglcontextlost', lost)
    const compile = (type: number, source: string) => {
      const shader = gl.createShader(type)
      if (!shader) throw new Error('Shader unavailable')
      shaders.push(shader)
      gl.shaderSource(shader, source)
      gl.compileShader(shader)
      if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS))
        throw new Error(
          `Shader compilation failed: ${gl.getShaderInfoLog(shader)}`
        )
      return shader
    }
    void Promise.all([
      loadImage(assetBaseUrl + layer.src),
      loadImage(assetBaseUrl + layer.mask)
    ])
      .then(([image, mask]) => {
        if (disposed) return
        if (
          image.naturalWidth !== mask.naturalWidth ||
          image.naturalHeight !== mask.naturalHeight
        )
          throw new Error('Water mask dimensions must match artwork')
        // WebGL specifies this capability as a numeric texture dimension.
        const maxSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number
        if (image.naturalWidth > maxSize || image.naturalHeight > maxSize)
          throw new Error('Texture too large')
        program = gl.createProgram()
        if (!program) throw new Error('Program unavailable')
        gl.attachShader(program, compile(gl.VERTEX_SHADER, waterVertexShader))
        gl.attachShader(
          program,
          compile(gl.FRAGMENT_SHADER, waterFragmentShader)
        )
        gl.linkProgram(program)
        if (!gl.getProgramParameter(program, gl.LINK_STATUS))
          throw new Error(
            `Shader linking failed: ${gl.getProgramInfoLog(program)}`
          )
        gl.useProgram(program)
        buffer = gl.createBuffer()
        if (!buffer) throw new Error('Buffer unavailable')
        gl.bindBuffer(gl.ARRAY_BUFFER, buffer)
        gl.bufferData(
          gl.ARRAY_BUFFER,
          new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
          gl.STATIC_DRAW
        )
        const position = gl.getAttribLocation(program, 'position')
        gl.enableVertexAttribArray(position)
        gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0)
        gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true)
        ;[image, mask].forEach((source, index) => {
          const texture = gl.createTexture()
          if (!texture) throw new Error('Texture unavailable')
          textures.push(texture)
          gl.activeTexture(gl.TEXTURE0 + index)
          gl.bindTexture(gl.TEXTURE_2D, texture)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
          gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
          gl.texImage2D(
            gl.TEXTURE_2D,
            0,
            gl.RGBA,
            gl.RGBA,
            gl.UNSIGNED_BYTE,
            source
          )
        })
        gl.uniform1i(gl.getUniformLocation(program, 'artwork'), 0)
        gl.uniform1i(gl.getUniformLocation(program, 'waterMask'), 1)
        gl.uniform2f(
          gl.getUniformLocation(program, 'imageSize'),
          image.naturalWidth,
          image.naturalHeight
        )
        gl.uniform1f(gl.getUniformLocation(program, 'strength'), layer.strength)
        gl.uniform1f(gl.getUniformLocation(program, 'shimmer'), layer.shimmer)
        const resolution = gl.getUniformLocation(program, 'resolution')
        const time = gl.getUniformLocation(program, 'time')
        const resize = () => {
          const rect = canvas.getBoundingClientRect()
          const scale = Math.min(
            window.devicePixelRatio || 1,
            1.5,
            1920 / Math.max(rect.width, 1),
            1080 / Math.max(rect.height, 1)
          )
          canvas.width = Math.max(1, Math.round(rect.width * scale))
          canvas.height = Math.max(1, Math.round(rect.height * scale))
          gl.viewport(0, 0, canvas.width, canvas.height)
          gl.uniform2f(resolution, canvas.width, canvas.height)
          gl.drawArrays(gl.TRIANGLES, 0, 6)
        }
        resize()
        observer = new ResizeObserver(resize)
        observer.observe(canvas)
        const start = performance.now()
        let previous = start
        const draw = (now: number) => {
          if (disposed) return
          if (readyRef.current && now - previous >= 1000 / 30) {
            previous = now
            gl.uniform1f(
              time,
              (((now - start) / 1000) * layer.speed) % (Math.PI * 20)
            )
            gl.drawArrays(gl.TRIANGLES, 0, 6)
          }
          frame = requestAnimationFrame(draw)
        }
        frame = requestAnimationFrame(draw)
        onLoaded(layer.id)
      })
      .catch(error => {
        if (!disposed) {
          logger.warn(
            'Wallpaper water effect unavailable; using still artwork.',
            error
          )
          setFallback(true)
        }
      })
    return () => {
      disposed = true
      cancelAnimationFrame(frame)
      observer?.disconnect()
      canvas.removeEventListener('webglcontextlost', lost)
      textures.forEach(texture => gl.deleteTexture(texture))
      shaders.forEach(shader => gl.deleteShader(shader))
      if (buffer) gl.deleteBuffer(buffer)
      if (program) gl.deleteProgram(program)
      // Keep the context usable for React StrictMode's setup/cleanup replay.
      // Resources are deleted above; the canvas/context is collected on unmount.
    }
  }, [layer, assetBaseUrl, onLoaded, fallback])

  return (
    <div
      className={styles.water}
      data-layer={layer.id}
      style={{
        left: `${layer.layout.left}%`,
        top: `${layer.layout.top}%`,
        width: cssSize(layer.layout.width),
        height: layer.layout.height ? cssSize(layer.layout.height) : undefined,
        aspectRatio: layer.layout.aspectRatio,
        opacity: layer.opacity
      }}
    >
      {fallback ? (
        <img
          className={styles.canvas}
          src={assetBaseUrl + layer.src}
          alt=''
          style={{ objectFit: 'cover' }}
          onLoad={() => onLoaded(layer.id)}
          onError={onFailed}
        />
      ) : (
        <canvas ref={ref} className={styles.canvas} aria-hidden='true' />
      )}
    </div>
  )
}
